import { VariantShippingMethodsProvider } from '@modules/ecommerce/context/VariantShippingMethodsContext';
import { ItemShippingProvider } from '@modules/ecommerce/context/ItemShippingContext';
import { useState } from 'react';
import VariantPOAMobile from '@modules/product/component/variant-pricing/VariantPOA.mobile';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import type Product from '@modules/product/type/product';
import useVariantPrice from '@modules/product/hook/use-variant-price';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import SamplePurchase from '@modules/product/component/sample-purchase/SamplePurchase';
import { useSampleStock } from '@modules/product/hook/use-sample-stock';
import * as S from './EnquirePurchaseStickyAction.style';
import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import PurchaseButtonGroup from '@modules/product/component/variant-purchase/PurchaseButtonGroup';
import LastAddedPopup from '@modules/ecommerce/component/shopping-cart-popup/LastAddedPopup.mobile';
import type { CartItem } from '@modules/ecommerce/type/cart-item';
import { useAtom } from 'jotai';
import { isShoppingCartMenuOpenState } from '@rocco/ui/website-header/states/ui-shopping-cart-menu';

interface EnquirePurchaseStickyAction {
    product: Product;
    primaryCTA: ProductPageLoaderData['primaryCTA'];
}

const EnquirePurchaseStickyAction = ({
    product,
    primaryCTA,
}: EnquirePurchaseStickyAction) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const [lastItemAdded, setLastItemAdded] = useState<CartItem | null>(null);

    const [cartPopupOpen, setCartPopupOpen] = useAtom(
        isShoppingCartMenuOpenState
    );
    const onCartPopupChange = (open: boolean) => {
        setCartPopupOpen(open);
    };

    const pickupLocations = product.Professional.PickupLocations;
    const pricingType = product.Pricing.PricingType;
    const shouldShowPurchase = product.EcommerceReady;

    const { hasPrice } = useVariantPrice(pricingType);
    const { selectedVariant, quantity } = useVariantSelectionState();
    const { canPurchase: sampleCanPurchase } = useSampleStock(selectedVariant);
    const hasShippingMethods = selectedVariant?.SampleShippingProfile
        ?.NoShipping
        ? false
        : (selectedVariant?.SampleShippingProfile?.ShippingMethods ?? [])
              .length > 0;
    const hasVariantShippingOptions =
        (selectedVariant?.ShippingProfile?.ShippingMethods ?? []).length > 0 ||
        (product.Professional.PickupLocations.length ?? 0) > 0;
    const hasSampleShippingOptions =
        hasShippingMethods || pickupLocations.length > 0;

    return (
        <div className={css(S.EnquirePurchaseSticky)}>
            {hasPrice && shouldShowPurchase ? (
                <VariantShippingMethodsProvider
                    selectedVariant={selectedVariant}
                    quantity={quantity}
                    estimatedSampleWeight={
                        product.Professional.SampleEstimatedWeight
                    }
                >
                    <ItemShippingProvider>
                        {pricingType === 'ecommercePricing' &&
                        hasVariantShippingOptions ? (
                            <PurchaseButtonGroup
                                onItemAdded={(item) => setLastItemAdded(item)}
                            />
                        ) : sampleCanPurchase && hasSampleShippingOptions ? (
                            <SamplePurchase productId={product.ID} />
                        ) : null}
                    </ItemShippingProvider>
                    {mobile && (
                        <LastAddedPopup
                            open={Boolean(cartPopupOpen && lastItemAdded)}
                            item={lastItemAdded}
                            onOpenChange={(_, props) => {
                                props &&
                                    !props.open &&
                                    onCartPopupChange(false);
                            }}
                        />
                    )}
                </VariantShippingMethodsProvider>
            ) : (
                <VariantPOAMobile
                    professional={{
                        title: product.Professional.Title,
                        id: product.Professional.ID,
                    }}
                    itemID={product.ID}
                    primaryCTA={primaryCTA}
                    isInServiceableArea={product.IsInServiceableArea}
                    product={product}
                />
            )}
        </div>
    );
};

export default EnquirePurchaseStickyAction;
